import { useEffect, useState } from "react";
import { Save, Building2, Landmark, FileBadge } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel, Field } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const GROUPS = [
  { title: "Identité de la société", icon: Building2, fields: [
    ["name", "Raison sociale *"], ["full_name", "Dénomination complète"], ["address", "Adresse"], ["city", "Ville / province / pays"],
    ["phone", "Téléphone 1"], ["phone2", "Téléphone 2"], ["email", "Email"], ["website", "Site web"],
  ] },
  { title: "Informations légales", icon: FileBadge, fields: [["rccm", "RCCM"], ["id_nat", "Id. Nat"], ["nif", "NIF"], ["tva_default", "TVA par défaut (%)", "number"]] },
  { title: "Coordonnées bancaires", icon: Landmark, fields: [["bank_name", "Banque"], ["bank_holder", "Titulaire du compte"], ["bank_account", "N° de compte / IBAN"], ["bank_swift", "Code SWIFT"]] },
];

const Title = ({ icon: Icon, text }) => <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-red-600" />{text}</span>;

export default function Settings() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/company").then((r) => setF(r.data)); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/settings/company", { ...f, tva_default: Number(f.tva_default) });
      setF(r.data);
      toast.success("Paramètres enregistrés — ils apparaîtront sur tous les PDF");
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  if (!f) return <p className="text-sm text-slate-500">Chargement…</p>;

  return (
    <form onSubmit={save} data-testid="settings-form">
      <PageHeader kicker="Administration" title="Paramètres société">
        <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="settings-save-btn"><Save className="mr-1 h-4 w-4" /> Enregistrer</Button>
      </PageHeader>
      <p className="mb-6 max-w-2xl text-sm text-slate-500">Ces informations sont imprimées sur les devis, bons de commande, factures et rapports d'intervention. Les champs laissés vides s'affichent « [à compléter] ».</p>
      <div className="grid gap-6 lg:grid-cols-2">
        {GROUPS.map((g) => (
          <Panel key={g.title} title={<Title icon={g.icon} text={g.title} />} className={g.fields.length > 4 ? "lg:row-span-2" : ""}>
            <div className="grid gap-4 sm:grid-cols-2">
              {g.fields.map(([k, l, type]) => (
                <Field key={k} label={l}>
                  <Input type={type || "text"} step="any" required={k === "name"} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`settings-${k}-input`} />
                </Field>
              ))}
            </div>
          </Panel>
        ))}
        <Panel title="Conditions de paiement (sur les factures)" className="lg:col-span-2">
          <Textarea rows={3} value={f.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="Ex. : paiement à 30 jours par virement bancaire." data-testid="settings-payment_terms-input" />
        </Panel>
      </div>
    </form>
  );
}
